import type { ActorSummary, ChangeLogEntry, FieldChange } from '@/types'

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  RESTORE: 'Restored',
  STATUS_CHANGE: 'Status Changed',
  BULK_UPDATE: 'Bulk Updated',
  IMPORT: 'Imported',
}

const ENTITY_LABELS: Record<string, string> = {
  PLAN: 'Plan',
  PLAN_VERSION: 'Version',
  LINE_ITEM: 'Line Item',
  QUOTE_REQUEST: 'Quote Request',
  COMMITMENT_LETTER: 'Commitment Letter',
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function formatAuditTitle(entry: ChangeLogEntry): string {
  const entity = ENTITY_LABELS[entry.entity_type] || entry.entity_type
  const action = ACTION_LABELS[entry.action] || entry.action

  // For status changes, try to show the new status from changes
  if (entry.action === 'STATUS_CHANGE') {
    const statusChange = entry.changes.find(c => c.field === 'status')
    if (statusChange?.new_value) {
      return `${entity} Status Changed to ${statusChange.new_value}`
    }
  }

  const label = entry.entity_display_name || entry.entity_name
  const name = label ? ` "${label}"` : ''
  return `${entity}${name} ${action}`
}

export function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function formatChangeValue(value: string | null): string {
  if (value === null || value === '') return '(empty)'
  if (UUID_RE.test(value)) return ''
  return value
}

export function formatChangeSummary(change: FieldChange): string {
  const field = formatFieldName(change.field)
  const oldVal = formatChangeValue(change.old_value)
  const newVal = formatChangeValue(change.new_value)

  // Both values are UUIDs or empty — just show the field was updated
  if (!oldVal && !newVal) return `${field}: updated`
  if (!oldVal && newVal) return `${field}: set to ${newVal}`
  if (oldVal && !newVal) return `${field}: ${oldVal} cleared`
  return `${field}: ${oldVal} → ${newVal}`
}

export function formatActorDisplay(actor: ActorSummary): string {
  if (!actor.id) return 'System'
  if (actor.display_name) return actor.display_name
  return `User ${actor.id.slice(0, 8)}...`
}

export function formatAuditTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}
